// @flow
import * as React from 'react';
import {
    Button,
    Column,
    Columns,
    Stack,
    Text,
    Tooltip
} from '@locus-taxy/neo-ui';

type Props = {
    children: React.Node,
    id: string,
    title?: string,
    collapsible?: boolean,
    onDuplicate?: (sectionId: string) => void,
    onDelete?: (sectionId: string) => void,
    onToggleCollapse?: (sectionId: string, collapsed: boolean) => void,
    collapsed?: boolean
};

export const Section = ({
    id,
    children,
    title,
    collapsed = false,
    onToggleCollapse,
    onDuplicate,
    onDelete
}: Props): React.Node => {
    const onCollapseToggle = React.useCallback(() => {
        onToggleCollapse?.(id, !collapsed);
    }, [collapsed, id, onToggleCollapse]);

    const onDeleteHandler = React.useCallback(() => {
        onDelete?.(id);
    }, [id, onDelete]);

    const onDuplicateHandler = React.useCallback(() => {
        onDuplicate?.(id);
    }, [id, onDuplicate]);
    return (
        <Stack
            space="small"
            align="stretch"
            _sx={{
                width: '100%',
                borderWidth: 'thin',
                borderStyle: 'solid',
                borderColor: 'rgba(17, 19, 24, 0.1)',
                p: 2
            }}
        >
            {title ? (
                <Columns alignY="center">
                    <Column width="fluid">
                        <Text
                            fontSize={0}
                            fontWeight="bold"
                            textTransform="uppercase"
                        >
                            {title}
                        </Text>
                    </Column>

                    <Column width="content">
                        <Columns>
                            {onToggleCollapse ? (
                                <Tooltip
                                    tooltip={collapsed ? ' Expand' : 'Collapse'}
                                >
                                    <Button
                                        appearance="icon"
                                        icon={
                                            collapsed
                                                ? 'arrow_chevron_down'
                                                : 'arrow_chevron_up'
                                        }
                                        onClick={onCollapseToggle}
                                    />
                                </Tooltip>
                            ) : null}
                            {onDelete ? (
                                <Tooltip tooltip="Delete">
                                    <Button
                                        appearance="icon"
                                        icon="delete"
                                        onClick={onDeleteHandler}
                                    />
                                </Tooltip>
                            ) : null}

                            {onDuplicate ? (
                                <Tooltip tooltip="Duplicate">
                                    <Button
                                        appearance="icon"
                                        icon="copy_content"
                                        onClick={onDuplicateHandler}
                                    />
                                </Tooltip>
                            ) : null}
                        </Columns>
                    </Column>
                </Columns>
            ) : null}

            {onCollapseToggle && collapsed ? null : children}
        </Stack>
    );
};

export default Section;
